import * as THREE from 'three';

/**
 * Döküm simülasyonu — voxel tabanlı motor sözleşmesi.
 * Ticari döküm yazılımları (MAGMASOFT/ProCAST/FLOW-3D) gibi grid (FDM/FVM)
 * yaklaşımı: kaviteyi voxelle → dolum cephesi (Faz 1) → ısı/katılaşma (Faz 2)
 * → kusur+rapor (Faz 3). Hepsi AYNI voxel kafesi üzerinde.
 */

// ─────────────────────────────────────────────────────────────────────────
// Voxel kafesi — tüm fazların ortak zemini
// ─────────────────────────────────────────────────────────────────────────

export interface IVoxelKafes {
  /** Hücre sayısı [nx, ny, nz] */
  boyut: [number, number, number];
  /** Hücre kenar uzunluğu (mm, dünya uzayı) */
  hucreMm: number;
  /** Grid başlangıç köşesi (dünya min) */
  origin: THREE.Vector3;
  /** Doluluk: 1 = parça (metal dolacak) içi, 0 = boş. Uzunluk nx*ny*nz. */
  doluluk: Uint8Array;
  /** Dolu hücre sayısı */
  doluSayi: number;
  /** Per-hücre yerel kesit kalınlığı (mm) — sıcak nokta/soğuma için. 0 = dolu değil/bilinmiyor. */
  kalinlik: Float32Array;
  /** Toplam parça hacmi (cm³) — kesin (nativeVolumeCm3) varsa ondan ölçeklenir */
  hacimCm3: number;
  /** Bölge tipi: 0 = parça, 1 = besleyici (riser), 2 = giriş kanalı. Yoksa hepsi parça. */
  bolgeTipi?: Uint8Array;
  /** Besleyici indeksi (yolluk sırası) — bolgeTipi=1 voxeller için; diğerleri -1. */
  besleyiciIdx?: Int16Array;
}

/** Bir dünya noktasının kafes lineer indeksini verir (yoksa -1) */
export function voxelIndeks(k: IVoxelKafes, p: THREE.Vector3): number {
  const ix = Math.floor((p.x - k.origin.x) / k.hucreMm);
  const iy = Math.floor((p.y - k.origin.y) / k.hucreMm);
  const iz = Math.floor((p.z - k.origin.z) / k.hucreMm);
  const [nx, ny, nz] = k.boyut;
  if (ix < 0 || iy < 0 || iz < 0 || ix >= nx || iy >= ny || iz >= nz) return -1;
  return ix + nx * (iy + ny * iz);
}

/** Hücre indisinden dünya merkez koordinatı (önceden ayrılmış out'a yazar) */
export function voxelMerkez(k: IVoxelKafes, lin: number, out: THREE.Vector3): THREE.Vector3 {
  const [nx, ny] = k.boyut;
  const ix = lin % nx;
  const iy = Math.floor(lin / nx) % k.boyut[1];
  const iz = Math.floor(lin / (nx * ny));
  out.set(
    k.origin.x + (ix + 0.5) * k.hucreMm,
    k.origin.y + (iy + 0.5) * k.hucreMm,
    k.origin.z + (iz + 0.5) * k.hucreMm,
  );
  return out;
}

// ─────────────────────────────────────────────────────────────────────────
// Faz 1 — Dolum (filling)
// ─────────────────────────────────────────────────────────────────────────

/** Dolum girişi (debi + opsiyonel akış profili: rampa/sinüs/puls) */
export interface IDolumGiris {
  pozisyon: THREE.Vector3;
  debiGramSn: number;
  /** Akış profili — 'sabit'|'rampa'|'sinus'|'puls' (yoksa 'sabit') */
  profil?: 'sabit' | 'rampa' | 'sinus' | 'puls';
  /** Zamanlı senaryo: giriş t=acilmaSn'de açılır (s). Yoksa 0. */
  acilmaSn?: number;
  /** Zamanlı senaryo: giriş t=kapanmaSn'de kapanır (s). Yoksa hiç. */
  kapanmaSn?: number;
}

/** Konum + yarıçaplı bölge (filtre/maça/hava deliği etki alanı) */
export interface IBolgeNokta {
  pozisyon: THREE.Vector3;
  capMm: number;
}

export interface IDolumGirdi {
  kafes: IVoxelKafes;
  /** Dolum giriş noktaları (dünya) — yolluk editöründeki dolum_girisi elemanları */
  girisler: IDolumGiris[];
  /** Yukarı (yerçekiminin tersi) yön */
  yukari: THREE.Vector3;
  /** Alaşım akışkanlık indeksi 0-100 (yüksek = daha iyi akar) */
  akiskanlik: number;
  /** Toplam debi g/s (dolum süresi ölçeği için) */
  toplamDebiGramSn: number;
  /** Yoğunluk g/cm³ (kütle/zaman için) */
  yogunlukGcm3: number;
  /** Filtreler — yakın voxellerde akış direnci uygular (ilk metali yavaşlatır) */
  filtreler?: IBolgeNokta[];
  /** Maçalar (core) — bu bölgedeki voxeller dolmaz (iç boşluk/engel) */
  macalar?: IBolgeNokta[];
  /** Hava delikleri — yakın hapsolan hava cepleri tahliye edilir (kusurdan düşer) */
  havalandirmalar?: IBolgeNokta[];
  /** Termal akış (non-izotermal): verilirse metal akarken soğur → cold shut/misrun + dolum sıcaklığı */
  termal?: IDolumTermal;
  /** İlerleme geri çağrısı 0-100 (async-parçalı çalışmada UI güncellemesi) */
  ilerle?: (yuzde: number) => void;
}

/** Non-izotermal dolum için termal parametreler (Flemings akışkanlık) */
export interface IDolumTermal {
  dokumSicaklik: number;   // pour/döküm sıcaklığı (°C)
  T_liquidus: number;      // °C — altına inen akan metal "soğuk" (cold shut)
  T_solidus: number;       // °C — altına inen metal donar, akışı keser (misrun)
  kalipSicaklik: number;   // °C — kalıp/ortam
  /** Soğuma şiddeti çarpanı (alaşım iletkenlik/akışkanlığa göre); varsayılan 1 */
  soguma?: number;
}

export interface IDolumSonuc {
  /** Per-hücre dolum zamanı (s) — Infinity = dolmadı (eksik dolum). Uzunluk nx*ny*nz. */
  dolumZamani: Float32Array;
  /** Toplam dolum süresi (s) */
  toplamSure: number;
  /** Hapsolan hava cepleri (dünya noktaları + büyüklük) */
  havaCepleri: { merkez: THREE.Vector3; hucreSayisi: number }[];
  /** Soğuk birleşme / eksik dolum riskli bölgeler (dünya + şiddet 0-1) */
  sogukBolgeler: { merkez: THREE.Vector3; siddet: number }[];
  /** Hiç dolmayan (misrun) hücre sayısı */
  dolmayanSayi: number;
  /** Per-hücre dolum anı sıcaklığı (°C) — termal akış açıksa; katılaşmaya başlangıç. Yoksa null. */
  varisSicaklik?: Float32Array | null;
  /** Soğuk birleşme (cold shut) bölgeleri — metal liquidus altında birleşti (dünya + şiddet) */
  coldShutBolgeler?: { merkez: THREE.Vector3; siddet: number }[];
}

export interface IDolumMotoru {
  hesapla(girdi: IDolumGirdi): Promise<IDolumSonuc>;
}

// ─────────────────────────────────────────────────────────────────────────
// Görselleştirme — dolum animasyonu (zaman ilerledikçe hücreler dolar)
// ─────────────────────────────────────────────────────────────────────────

export interface IDolumGorselOpts {
  kok: THREE.Object3D;          // sahneye eklenecek kök
  kafes: IVoxelKafes;
  sonuc: IDolumSonuc;
  /** Renk modu: 'zaman' (dolum sırası) | 'sicaklik' (faz 2) */
  renkModu?: 'zaman' | 'sicaklik';
  /** Termal kareler — verilirse TÜM voxeller görünür, her karede gerçek sıcaklığa göre renklenir (heat-map). */
  sicaklikKareler?: { sure: number; T8: Uint8Array }[];
  sicaklikMin?: number;         // °C — T8 dequantize
  sicaklikMax?: number;         // °C
  /**
   * Çekme boşluğu kareleri (B8: per-voxel boşluk oranı 0-255) — sicaklikKareler
   * ile AYNI zaman tabanında. Boşalan voxel (besleyici pipe / çekme boşluğu)
   * animasyonda kaybolur → besleyici seviyesinin düşüşü gözle görülür.
   */
  boslukKareler?: { sure: number; B8: Uint8Array }[];
  /** Environment map (PMREM) — metalik yansıma/parlaklık için */
  envMap?: THREE.Texture | null;
  /** Voxel görünüm boyut faktörü (hücreye göre yarıçap, 0.3 küçük → 1.0 örtüşen). Varsayılan 0.72. */
  boyutFaktor?: number;
  /** Renk parlaklığı çarpanı (0.5 soluk → 2 parlak). Varsayılan 1. */
  parlaklik?: number;
  /** Akkor glow halesi (additive, paylaşımlı buffer) — bloom benzeri. Varsayılan true. */
  glow?: boolean;
  /** Pürüzsüz yüzey (surface nets) — voxel yerine sürekli sıvı yüzeyi. Varsayılan false. */
  pruzsuz?: boolean;
  /** Pürüzsüz yüzey kalınlığı (dilation adımı, 1-4) — ince parçayı doluya yaklaştırır. Varsayılan 1. */
  pruzsuzKalinlik?: number;
}

export interface IDolumGorsel {
  /** t anına kadar dolan hücreleri göster (saniye) */
  zamaniAyarla(t: number): void;
  /** Toplam süre */
  sure(): number;
  dispose(): void;
}
